/**
 * EvolveAgentModal — Phase E of PLAN-ues-integration.md.
 *
 * Flow:
 *   1. User enters a "reason" (what triggered the evolve — new model, API
 *      migration, etc.)
 *   2. Clicking "Gather Context" calls POST /api/v1/agents/{name}/evolve
 *      which dispatches UES with intent=evolve and returns the extraction
 *      (subject + integration-surface graph + LLM-ready context document +
 *      confidence).
 *   3. User reviews the extraction (confidence, subject, markdown preview).
 *   4. Clicking "Build V2" approves the extraction and calls
 *      /evolve/execute which dispatches auto-orchestrator with a
 *      "Build V2 of {agent}" goal. Returns the orchestration request_id.
 *   5. Parent navigates to /playground?replay=<request_id> to watch the
 *      build live.
 */

import { useState, useEffect } from 'react'
import { XMarkIcon, SparklesIcon, ArrowPathIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { clsx } from 'clsx'
import toast from 'react-hot-toast'
import { apiClient } from '@/services/api'
import Button from '@/components/common/Button'

interface EvolveAgentModalProps {
  open: boolean
  agentName: string
  onClose: () => void
  onDispatched: (requestId: string) => void
}

interface UESResult {
  success?: boolean
  extraction_id?: string | null
  subject?: { name?: string; type?: string } | null
  context_document?: string
  confidence?: number
  metadata?: { degraded?: boolean; reason?: string }
}

export default function EvolveAgentModal({ open, agentName, onClose, onDispatched }: EvolveAgentModalProps) {
  const [reason, setReason] = useState('')
  const [fetching, setFetching] = useState(false)
  const [building, setBuilding] = useState(false)
  const [result, setResult] = useState<UESResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      // Reset on close
      setReason('')
      setResult(null)
      setError(null)
      setFetching(false)
      setBuilding(false)
    }
  }, [open])

  if (!open) return null

  const handleGather = async () => {
    if (!reason.trim()) {
      toast.error('Reason is required')
      return
    }
    setFetching(true)
    setError(null)
    setResult(null)
    try {
      const resp = await (apiClient as any).client.post(
        `/api/v1/agents/centralized/${agentName}/evolve`,
        { reason },
      )
      setResult(resp.data)
      if (resp.data?.metadata?.degraded) {
        toast('UES returned degraded context — you can still proceed')
      } else {
        toast.success('Context gathered')
      }
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to gather context'
      setError(msg)
      toast.error(msg)
    } finally {
      setFetching(false)
    }
  }

  const handleBuild = async () => {
    if (!result) return
    setBuilding(true)
    setError(null)
    try {
      const resp = await (apiClient as any).client.post(
        `/api/v1/agents/centralized/${agentName}/evolve/execute`,
        {
          extraction_id: result.extraction_id,
          context_document: result.context_document,
          reason,
          approve: true,
        },
      )
      const requestId = resp.data?.orchestration_request_id || resp.data?.result?.request_id
      if (requestId) {
        toast.success('V2 build dispatched — watching live in Playground')
        onDispatched(requestId)
      } else {
        toast.error('Dispatch returned no request_id')
      }
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to dispatch V2 build'
      setError(msg)
      toast.error(msg)
    } finally {
      setBuilding(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-card rounded-xl border border-border shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border shrink-0">
          <div>
            <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
              <SparklesIcon className="w-5 h-5 text-primary" />
              Evolve agent · <span className="font-mono">{agentName}</span>
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Gather NeuralGraph context via UES, then dispatch auto-orchestrator to build V2.
            </p>
          </div>
          <button onClick={onClose} className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-muted">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground block mb-1">
              Reason (what triggered this evolve?)
            </label>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={3}
              placeholder="e.g. new Gemini embedding model released, need to migrate from embedding-2 to embedding-3"
              className="w-full text-sm px-3 py-2 rounded-md border border-border bg-card resize-y"
              disabled={fetching || building}
            />
          </div>

          {error && (
            <div className="text-xs text-red-500 bg-red-500/10 border border-red-500/20 rounded p-2 flex items-start gap-2">
              <ExclamationTriangleIcon className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {result && (
            <div className="space-y-3 pt-2 border-t border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {result.metadata?.degraded ? (
                    <ExclamationTriangleIcon className="w-4 h-4 text-amber-500" />
                  ) : (
                    <CheckCircleIcon className="w-4 h-4 text-emerald-500" />
                  )}
                  <span className="text-sm font-semibold text-foreground">
                    {result.metadata?.degraded ? 'Degraded extraction' : 'Extraction ready'}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                  <span>confidence: <strong className="text-foreground">{(result.confidence ?? 0).toFixed(2)}</strong></span>
                  {result.subject?.name && (
                    <span>subject: <strong className="text-foreground">{result.subject.name}</strong>{result.subject.type && <span className="text-muted-foreground/60"> · {result.subject.type}</span>}</span>
                  )}
                </div>
              </div>

              {result.extraction_id && (
                <div className="text-[10px] font-mono text-muted-foreground">
                  extraction_id: {result.extraction_id}
                </div>
              )}

              <div>
                <p className="text-[10px] font-semibold text-muted-foreground uppercase mb-1">Context document</p>
                <pre className={clsx(
                  'text-xs rounded p-3 whitespace-pre-wrap max-h-72 overflow-y-auto font-mono border',
                  result.metadata?.degraded
                    ? 'bg-amber-500/5 border-amber-500/20'
                    : 'bg-emerald-500/5 border-emerald-500/20'
                )}>
                  {result.context_document || '(no context document returned)'}
                </pre>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-border shrink-0">
          <span className="text-[11px] text-muted-foreground">
            UES intent: <code>evolve</code>
          </span>
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={onClose} disabled={fetching || building}>Close</Button>
            {!result ? (
              <Button onClick={handleGather} disabled={fetching || !reason.trim()}>
                {fetching ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <SparklesIcon className="w-4 h-4" />}
                <span className="ml-1.5">{fetching ? 'Gathering…' : 'Gather context'}</span>
              </Button>
            ) : (
              <Button onClick={handleBuild} disabled={building}>
                {building ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <SparklesIcon className="w-4 h-4" />}
                <span className="ml-1.5">{building ? 'Dispatching…' : 'Build V2'}</span>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
